import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isDev } from "../../config/environment";
import * as yup from "yup";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { useTSMForgotPassword } from "../../hooks/useTSAuth";

// Validation schema
const loginSchema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

interface LoginFormData {
  email: string;
  password: string;
}

export const PageLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [forgotPW, setForgotPW] = useState<boolean>(false);

  // Get the intended destination from location state
  const from = "/dashboard";

  const forgotPWMutation = useTSMForgotPassword();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (forgotPW) {
      setValue("password", "temppass");
    } else {
      setValue("password", "");
    }
  }, [forgotPW, setValue]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      if (forgotPW) {
        await forgotPWMutation.mutateAsync(data.email);
        setError(
          "A password reset email will be sent to the supplied email if present in the system."
        );
      } else {
        console.log("ATTEMPTING TO LOG IN");
        await login(data.email, data.password);
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 400,
          }}
        >
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: "center" }}>
              <img
                src="/nucleus.svg"
                alt="Nucleus Logo"
                style={{ width: "100%", height: "auto" }}
              />
              {/* <Typography variant="h4" component="h1" gutterBottom>
                NUCLEUS
              </Typography> */}
              <Typography variant="h6" color="text.secondary">
                Sign in to your account
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={forgotPW}
                      onChange={(e, c) => setForgotPW(c)}
                    />
                  }
                  label="Forgot Password"
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Address"
                      type="email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={isSubmitting || loading}
                      autoComplete="email"
                      autoFocus
                    />
                  )}
                />
                {!forgotPW && (
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={isSubmitting || loading}
                        autoComplete="current-password"
                      />
                    )}
                  />
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting || loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {forgotPW ? "Contacting Server..." : "Signing in..."}
                    </>
                  ) : forgotPW ? (
                    "Forgot Password"
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Stack>
            </Box>

            {/* Footer */}
            {/* <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Secure login with cookie-based authentication
              </Typography>
            </Box> */}
          </Stack>
        </Paper>

        {/* Development Info */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            {isDev ? "!!!DEVELOPMENT ENVIRONMENT!!!" : ""}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};
